import {
  Col,
  DatePicker,
  Form,
  Input,
  InputProps,
  message,
  Row,
  Select,
  Spin,
  Switch,
  TreeSelect,
  Upload,
  UploadFile,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import SlateEditor from '@tools/components/slateEditor.tsx';
import {
  filterOption as filterOptionFromTools,
  getLabelValidateField,
  handleOptionsSort as handleOptionsSortFromTools,
  validateFileType,
} from '@tools/utils/common.tsx';
import {
  CustomUploadFile,
  FieldType, LabelType,
  OptionType,
  TreeOptionFieldType,
} from '@tools/types/commonFields.types.ts';
import { FieldValue } from '@tools/types/common.types';
import { InboxOutlined } from '@ant-design/icons';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  memo,
  useMemo,
} from 'react';
import { isEqual } from 'lodash';
import {
  DndContext,
  PointerSensor,
  useSensor,
  Active,
  Over,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RcFile } from 'antd/lib/upload';
import metadata from 'libphonenumber-js/metadata.min.json';
import 'react-phone-number-input/style.css';
import PhoneInputCore from 'react-phone-number-input/input-core';
import { logError, logInfo } from '@tools/LogsHandler.tsx';
import { conns } from '@cfg/axiosConfig.tsx';

/**
 * ---------------------------------------------
 *  Helpers & Small Components
 * ---------------------------------------------
 */
// ⛏️ FIX: cortar después del PRIMER '_' (no el último)
export const toLogicalName = (internal: string) => {
  const i = internal.indexOf('_');
  return i >= 0 ? internal.slice(i + 1) : internal;
};


export const ProgressSpinner: React.FC<{ isLoading: boolean }> = ({
  isLoading,
}) => {
  const [percent, setPercent] = useState(0);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (isLoading) {
      intervalId = setInterval(() => {
        setPercent((prev) => (prev + 2) % 100);
      }, 100);
    } else {
      setPercent(0);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isLoading]);

  return <Spin spinning={isLoading} percent={percent} fullscreen />;
};


/**
 * Sanitiza la lista para que:
 * 1.  value y label nunca sean null / undefined
 * 2.  no existan valores duplicados (AntD lo prohíbe)
 */
const sanitizeOptions = <T extends { value: unknown; label: unknown }>(list: T[]) => {
  const seen = new Set<unknown>();
  return list.filter(
    (o) =>
      o.value !== undefined &&
      o.value !== null &&
      o.label !== undefined &&
      o.label !== null &&
      !seen.has(o.value) &&
      seen.add(o.value),
  );
};

export const getOptions = async (
  urlName: string,
  fields: { labelName: string; valueName: string } = { labelName: 'name', valueName: 'id' },
  data?: object,
): Promise<OptionType[]> => {
  try {
    const { data: datalist } = await conns(urlName).get(data);
    logInfo('getOptions - ', urlName, 'data', data, 'datalist', datalist);
    return sanitizeOptions(
      datalist.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (d: any): OptionType => ({
          label: d[fields.labelName],
          value: d[fields.valueName],
        }),
      ),
    );
  } catch (error) {
    logError(`Error get Options ${urlName}: ${error}`);
    return [];
  }
};


export const getTreeOptions = async (urlName: string,
  fields: { labelName: string, valueName: string, childrenName: string } = { labelName: 'name', valueName: 'id', childrenName: 'children' },
  data?: object): Promise<TreeOptionFieldType[]> => {

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const buildTreeData = (datalist: any[]): TreeOptionFieldType[] =>
    datalist
      // .filter((d) => d[fields.valueName] !== undefined && d[fields.valueName] !== null)
      .map((dataItem) => ({
        title: dataItem[fields.labelName],
        label: dataItem[fields.labelName],          // ← rc-tree-select lo usa internamente
        value: dataItem[fields.valueName],
        key: String(dataItem[fields.valueName]),    // ← clave única obligatoria
        children: dataItem[fields.childrenName] ? buildTreeData(dataItem[fields.childrenName]) : [],
      }));


  try {
    logInfo('*** getOptions -> urlName: ', urlName, { fields, data });
    const response = await conns(urlName).get(data);
    logInfo('getTreeOptions - response.data: ', response.data);
    return buildTreeData(response.data);
  } catch (error) {
    logError(`Error get Options ${urlName}: ${error}`);
    return [];
  }
};


// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const loadFieldOptions = async (key: string, field: any, setSelectOptions: React.Dispatch<React.SetStateAction<{ [key: string]: OptionType[] | TreeOptionFieldType[] }>>) => {
  if (field.options && field.options.length > 0) {
    logInfo('No requiere carga select options de ', field.name, 'Options: ', field.options, 'key: ', key);
    setSelectOptions(prev => ({ ...prev, [key]: field.options as OptionType[] | TreeOptionFieldType[] }));
  } else {
    if (field.optionsHandlerUrl && field.type) {
      let options;
      if (['select', 'select-with-search'].includes(field.type)) {
        options = await getOptions(field.optionsHandlerUrl);
      } else if (field.type === 'tree-select') {
        options = await getTreeOptions(field.optionsHandlerUrl, {
          labelName: 'name',
          valueName: 'id',
          childrenName: 'children'
        });
      }

      if (options) {
        field.options = options;
        setSelectOptions(prev => ({ ...prev, [key]: options }));
        logInfo('Se cargó select options de ', field.name, 'Options: ', options);
      }
    }
  }
};



export const PhoneNumberInput: React.FC<InputProps> = ({
  placeholder,
  maxLength = 15,
  className,
  style,
  onChange,
  value = '',
  ...restProps
}) => {
  const [phoneNumber, setPhoneNumber] = useState<string>(value as string);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = e.target.value.replace(/[^0-9\s\-+]/g, '');
    setPhoneNumber(formatted);
    if (onChange) {
      onChange({ ...e, target: { ...e.target, value: formatted } } as unknown as React.ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <Input
      type="tel"
      placeholder={placeholder}
      maxLength={maxLength} // Limitar la longitud si es necesario
      value={phoneNumber}
      onChange={handleChange}
      className={className}
      style={style}
      {...restProps} // Pasar cualquier otra prop que pueda venir
    />
  );
};



interface CodeInputProps {
  digits: number;
  onComplete: (code: string) => void;
  cleanCodeInput?: () => void;
  digitBox?: React.HTMLAttributes<never>;
  inputType?: 'numeric' | 'alphanumeric' | 'all';
  inputIdPrefix?: string;
  inputAriaLabel?: string | ((index: number, digits: number) => string);
  resetCode?: boolean;
  setResetCode?: (status: boolean) => void;
  onChange?: (value: string) => void;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

export const CodeInput: React.FC<CodeInputProps> = ({
  digits,
  digitBox,
  inputType = 'numeric',
  inputIdPrefix = 'digit',
  inputAriaLabel,
  onComplete,
  resetCode = false,
  setResetCode,
  onChange,
  disabled = false,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}) => {
  const [code, setCode] = useState<string[]>(Array(digits).fill(''));
  const getInputId = (index: number) => `${inputIdPrefix}-${index}`;
  const getInputAriaLabel = (index: number) => {
    if (typeof inputAriaLabel === 'function') {
      return inputAriaLabel(index, digits);
    }
    return inputAriaLabel ?? ariaLabel;
  };

  /** Focus handling */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setFocus = (newCode: any[], index?: number) => {
    const nextIndex =
      index ?? newCode.findIndex((val: string) => val === '');
    if (nextIndex !== -1 && nextIndex < digits) {
      document.getElementById(getInputId(nextIndex))?.focus();
    } else if (nextIndex === -1) {
      onComplete(newCode.join(''));
    }
  };

  /** Character validation */
  const isValidCharacter = (value: string) => {
    switch (inputType) {
      case 'numeric':
        return /^[0-9]$/.test(value);
      case 'alphanumeric':
        return /^[a-zA-Z0-9]$/.test(value);
      case 'all':
      default:
        return /.+/.test(value);
    }
  };

  /** Events */
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const { value } = e.target;
    if (isValidCharacter(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      onChange?.(newCode.join(''));
      if (index === digits - 1) onComplete(newCode.join(''));
      else setFocus(newCode, index + 1);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newCode = [...code];
      if (newCode[index] === '') {
        if (index > 0) {
          newCode[index - 1] = '';
          setCode(newCode);
          setFocus(newCode, index - 1);
        }
      } else {
        // Si el campo actual no está vacío, bórralo y mantén el foco en el mismo
        newCode[index] = '';
        setCode(newCode);
      }
      onChange?.(newCode.join(''));
    }
  };

  // Paste / Input events for mobile support
  const insertData = (data: string) => {
    const allowed = data.split('').filter(isValidCharacter);
    const newCode = [...code];
    for (let i = 0; i < digits && i < allowed.length; i++) {
      newCode[i] = allowed[i];
    }
    setCode(newCode);
    setFocus(newCode);
    onChange?.(newCode.join(''));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    insertData(e.clipboardData.getData('text'));
  };

  const handleInputMobile = (e: React.FormEvent<HTMLInputElement>) => {
    insertData(e.currentTarget.value);
  };

  /** Reset logic */
  useEffect(() => {
    if (resetCode && setResetCode) {
      setCode(Array(digits).fill(''));
      setFocus(Array(digits).fill(''), 0);
      setResetCode(false);
    }
  }, [resetCode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="d-flex justify-content-center">
      {Array.from({ length: digits }).map((_, idx) => (
        <div
          key={idx}
          className={digitBox?.className}
          style={digitBox?.style || { marginRight: 5 }}
        >
          <Input
            id={getInputId(idx)}
            type={inputType === 'numeric' ? 'number' : 'text'}
            inputMode={inputType === 'numeric' ? 'numeric' : undefined}
            aria-label={getInputAriaLabel(idx)}
            aria-describedby={ariaDescribedBy}
            maxLength={1}
            value={code[idx]}
            onChange={(e) => handleInputChange(e, idx)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            onPaste={handlePaste}
            onInput={handleInputMobile}
            onClick={() => setFocus(code)}
            disabled={disabled}
            style={{ appearance: 'none', textAlign: 'center' }}
            className="border border-1 rounded w-100 h_050px text-center h3 font-monospace fw-bolder text-info"
          />
        </div>
      ))}
    </div>
  );
};

/**
 * ---------------------------------------------
 *  Upload with drag, sort & caption
 * ---------------------------------------------
 */

interface SortableUploadItemProps {
  file: CustomUploadFile;
  originNode: React.ReactElement;
  onCaptionChange: (uid: string, caption: string) => void;
}

const SortableUploadItem: React.FC<SortableUploadItemProps> = memo(
  ({ file, originNode, onCaptionChange }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: file.uid });

    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      cursor: 'move',
      opacity: isDragging ? 0.8 : 1,
      zIndex: isDragging ? 1000 : 1,
      // ELIMINAR: contenedor aspect_1-1 que causaba superposición
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`my-2 ${isDragging ? 'is-dragging' : ''}`}
        {...attributes}
        {...listeners}
      >
        <div className="contenedor aspect_1-1">
          <div className="contenido">{originNode}</div>
        </div>
        <Input
          placeholder="Etiqueta"
          value={file.caption ?? ''}           // ← antes: defaultValue
          onChange={(e) => onCaptionChange(file.uid, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          className="mt-2"
        />
      </div>
    );
  },
);
SortableUploadItem.displayName = 'SortableUploadItem';

const formatInitialValues = (
  initialValues: Array<{
    pk: number;
    caption?: string;
    thumbnails?: { default?: string };
    url?: string;
    thumbUrl?: string;
    name?: string;
  }> = [],
): CustomUploadFile[] =>
  (initialValues || [])
    .filter((it) => !!it) // por si viene algún null/undefined
    .map((item, idx) => {
      const url =
        item.thumbnails?.default ??
        item.url ??
        item.thumbUrl ??
        ''; // puede quedar vacío si no hay nada

      return {
        pk: item.pk,
        uid: String(item.pk ?? idx),
        name: item.caption || item.name || `Image ${idx + 1}`,
        status: 'done',
        url,                  // AntD usa url para mostrar miniatura si es remota
        thumbUrl: url || undefined,
        caption: item.caption || '',
        order: idx,
      } as CustomUploadFile;
    });

interface UploadDragerSorteableProps {
  id?: string;
  name: string;
  className?: string;
  handleChange: (name: string, value: CustomUploadFile[]) => void;
  initialValue?: Array<{
    pk: number;
    caption?: string;
    thumbnails?: { default?: string };
    url?: string;
    thumbUrl?: string;
    name?: string;
  }>;
  accept?: string[];
  handleRemove?: (name: string, value: CustomUploadFile | undefined) => void;
  value?: CustomUploadFile[];
  'aria-describedby'?: string;
  'aria-invalid'?: 'true' | 'false';
}

export const UploadDragerSorteable: React.FC<UploadDragerSorteableProps> = ({
  id,
  name,
  className,
  handleChange,
  initialValue = [],
  accept = ['*'],
  handleRemove,
  value,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}) => {
  const MAX_FILES = 5;

  // 1) Derivar initialFileList a partir de initialValue
  const initialFileList = useMemo(() => formatInitialValues(initialValue), [initialValue]);

  const isControlled = value !== undefined;

  // 2) Estado interno con lista actual
  const [fileList, setFileList] = useState<CustomUploadFile[]>(() => value ?? initialFileList);

  // Flag para evitar eco cuando el cambio viene del usuario
  const shouldNotifyRef = useRef(false);

  // 3) Sincronizar desde props SOLO si cambia realmente (deep-compare)
  useEffect(() => {
    if (isControlled) return;
    if (!isEqual(fileList, initialFileList)) {
      setFileList(initialFileList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFileList, isControlled]); // (no dependemos de fileList para no crear lazo)

  useEffect(() => {
    if (!isControlled) return;
    if (!value?.length) {
      if (fileList.length) {
        setFileList([]);
      }
      return;
    }
    if (!isEqual(value, fileList)) {
      setFileList(value);
    }
  }, [fileList, isControlled, value]);

  // 4) Sensor DnD
  const sensor = useSensor(PointerSensor, { activationConstraint: { distance: 10 } });

  // 5) Método centralizado para actualizar lista y notificar al padre SOLO en cambios del usuario
  const updateList = (list: CustomUploadFile[]) => {
    if (isEqual(fileList, list)) return; // sin cambios reales
    shouldNotifyRef.current = true;      // marcar que el origen es el usuario
    setFileList(list);
    // Notificar inmediatamente al padre evitando doble render
    handleChange(name, list);
    shouldNotifyRef.current = false;
  };

  // 6) Drag end → reordenar
  const onDragEnd = ({ active, over }: { active: Active; over: Over | null }) => {
    if (!over || active.id === over.id) return;
    const reordered = arrayMove(
      fileList,
      fileList.findIndex((i) => i.uid === active.id),
      fileList.findIndex((i) => i.uid === over.id),
    ).map((f, idx) => ({ ...f, order: idx }));
    updateList(reordered);
  };

  // 7) Eliminar
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const localHandleRemove = (file: UploadFile<any>) => {
    const newList = fileList
      .filter((f) => f.uid !== file.uid)
      .map((f, idx) => ({ ...f, order: idx }));
    if (isEqual(fileList, newList)) return true;
    updateList(newList);
    handleRemove?.(name, file as CustomUploadFile);
    return true;
  };

  // 8) Validación antes de subir
  const beforeUpload = (file: RcFile) => {
    if (fileList.length >= MAX_FILES) {
      message.error(`Solo puedes subir un máximo de ${MAX_FILES} archivos.`).then();
      return Upload.LIST_IGNORE;
    }
    return validateFileType(file, accept) || Upload.LIST_IGNORE;
  };

  // 9) Cambiar etiqueta (caption)
  const handleCaptionChange = (uid: string, caption: string) => {
    const updated = fileList.map((f) => (f.uid === uid ? { ...f, caption } : f));
    updateList(updated);
  };

  // 10) Helper para URL de previsualización
  const getFileUrl = (file: any): string => { // eslint-disable-line @typescript-eslint/no-explicit-any
    if (file.url && !file.originFileObj) return file.url;

    // If it's a new file being uploaded, create object URL for preview
    if (file.originFileObj && file.status !== 'removed') {
      try {
        return URL.createObjectURL(file.originFileObj);
      } catch (error) {
        console.error('Error creating object URL:', error);
        return file.thumbUrl || '';
      }
    }

    // If it has thumbUrl, use it
    if (file.thumbUrl) return file.thumbUrl;

    // Fallback
    return '';
  };

  // ⚠️ IMPORTANTE:
  // — Eliminado el efecto "Keep parent informed on mount/change"
  //   que hacía: handleChange(name, initialFileList)
  //   Ese efecto generaba eco con el padre y causaba el loop.
  // — Ahora SOLO notificamos en acciones del usuario (updateList).

  return (
    <DndContext sensors={[sensor]} onDragEnd={onDragEnd}>
      <SortableContext items={fileList.map((f) => f.uid)} strategy={verticalListSortingStrategy}>
        <Upload.Dragger
          id={id}
          className={className}
          style={{ width: '100%', maxWidth: '100%' }}
          listType="picture-card"
          multiple
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          beforeUpload={beforeUpload}
          onChange={({ fileList: newFileList }) => {
            let next = newFileList;
            if (next.length > MAX_FILES) {
              next = next.slice(0, MAX_FILES);
              message.error(`Solo puedes subir un máximo de ${MAX_FILES} archivos.`).then();
            }

            // Fusionar por uid con el estado previo para NO perder metadatos (caption, etc.)
            const processedList = next.map((file, idx) => {
              const incoming = file as CustomUploadFile;
              const prev = fileList.find(p => p.uid === incoming.uid);   // ← mismo uid

              // Preferí la URL ya calculada del previo si existe (evita recrear innecesariamente)
              const url = getFileUrl(prev ?? incoming);

              return {
                ...(prev ?? {}),                  // conserva caption, u otros campos custom previos
                ...incoming,                      // actualiza status, percent, etc. de AntD
                url,
                thumbUrl: url,
                order: idx,
                caption: (prev?.caption ?? incoming.caption ?? ''), // preserva etiqueta si venía del input
                // ⚠️ NO seteamos uid aquí: dejamos el de AntD (stable para ese file)
              } as CustomUploadFile;
            });

            updateList(processedList);
          }}
          fileList={fileList}
          onRemove={localHandleRemove}
          itemRender={(originNode, file) => (
            <SortableUploadItem
              key={file.uid}
              file={file as CustomUploadFile}
              originNode={originNode}
              onCaptionChange={handleCaptionChange}
            />
          )}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Haz clic o arrastra las imágenes a esta área</p>
          <p className="ant-upload-hint">Puedes subir hasta {MAX_FILES} archivos.</p>
        </Upload.Dragger>
      </SortableContext>
    </DndContext>
  );
};

/* ===============================================================
 * Hook useFieldOptions – carga y cachea opciones
 * =============================================================== */
type OptionList = OptionType[] | TreeOptionFieldType[];

const fieldOptionsCache = new Map<string, OptionList>();

export function useFieldOptions(field: FieldType): OptionList | undefined {
  const [options, setOptions] = useState<OptionList | undefined>(
    field.options,
  );
  const cacheKey = useMemo(
    () => `${field.optionsHandlerUrl || 'static'}-${field.type}`,
    [field.optionsHandlerUrl, field.type],
  );

  // Keep internal state in sync with external option arrays
  useEffect(() => {
    if (Array.isArray(field.options) && field.options.length) {
      fieldOptionsCache.set(cacheKey, field.options as OptionList);
      setOptions(field.options as OptionList);
    }
  }, [cacheKey, field.options]);

  useEffect(() => {
    let isMounted = true;
    if (options?.length || !field.optionsHandlerUrl) return;

    const cached = fieldOptionsCache.get(cacheKey);
    if (cached?.length) {
      if (!field.options) {
        field.options = cached;
      }
      setOptions(cached);
      return;
    }

    (async () => {
      try {
        let fetched: OptionList = [];
        if (['select', 'select-with-search'].includes(field.type || '')) {
          fetched = await getOptions(field.optionsHandlerUrl!);
        } else if (field.type === 'tree-select') {
          fetched = await getTreeOptions(field.optionsHandlerUrl!);
        }
        if (isMounted) {
          field.options = fetched;
          fieldOptionsCache.set(cacheKey, fetched);
          setOptions(fetched);
        }
      } catch (err) {
        logError(`useFieldOptions: ${err}`);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [cacheKey, field.optionsHandlerUrl, options?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return options;
}

/**
 * ---------------------------------------------
 *  RenderFormField
 * ---------------------------------------------
 */

const { Search } = Input;
const { RangePicker } = DatePicker;

interface RenderFormFieldProps {
  fieldKey: string;
  field: FieldType;
  many?: boolean;
  handleChange: (name: string, value: FieldValue, label?: LabelType) => void;
  filterOption?: (input: string, option: unknown) => boolean;
  handleOptionsSort?: (optionA: OptionType, optionB: OptionType) => number;
  initialValue?: FieldValue;
  errorMessage?: string;
  handleRemove?: (name: string, value: FieldValue) => void;
  handleAutoComplete?: (name: string, value: FieldValue) => void;
  disabled?: boolean;
}

export const RenderFormField: React.FC<RenderFormFieldProps> = ({
  fieldKey,
  field,
  many = false,
  handleChange,
  filterOption = filterOptionFromTools,
  handleOptionsSort = handleOptionsSortFromTools,
  initialValue,
  errorMessage,
  handleRemove,
  handleAutoComplete,
  disabled,
}) => {
  /* -------- opciones -------- */
  const options = useFieldOptions(field);

  /* -------- estado -------- */
  const [fieldValue, setFieldValue] = useState<FieldValue>(initialValue);
  const [valueLabel, setValueLabel] = useState<string | number | boolean | React.ReactElement | undefined>();
  const [fieldFocused, setFieldFocused] = useState(false);

  /* -------- efects -------- */
  useEffect(() => {
    setFieldValue(initialValue);
    if (many && options?.length) {
      if (Array.isArray(initialValue)) {
        // Para arrays, crear un string con los labels concatenados
        const labels = initialValue
          .map(val => (options as OptionType[]).find(o => o.value === val)?.label)
          .filter(Boolean)
          .join(', ');
        if (labels) {
          setValueLabel(labels);
          logError('RenderFormField - Array labels encontrados:', {
            fieldName: field.name,
            values: initialValue,
            labels
          });
        }
      } else {
        // Para valores simples
        const opt = (options as OptionType[]).find((o) => o.value === initialValue);
        if (opt) {
          setValueLabel(opt.label);
          /*// TODO: SOLO PARA DEBUG
          logError('RenderFormField - Single label encontrado:', {
            fieldName: field.name,
            value: initialValue,
            label: opt.label
          });/**/
        }
      }
    }
  }, [initialValue, many, options]);   // eslint-disable-line react-hooks/exhaustive-deps

  /** Autocomplete global support */
  useEffect(() => {
    if (handleAutoComplete && !fieldFocused) handleAutoComplete(field.name, fieldValue);
  }, [fieldValue]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Validate value when options change */
  useEffect(() => {
    if (!['select', 'select-with-search'].includes(field.type || '')) return;
    if (!options?.length) return;

    if (fieldValue === undefined || fieldValue === null) return;

    let valid = false;

    // ✅ Manejar arrays (selects múltiples) y valores simples
    if (Array.isArray(fieldValue)) {
      // Para arrays, verificar que todos los valores existan en las opciones
      // Solo validar si el array no está vacío
      if (fieldValue.length === 0) {
        valid = true; // Array vacío es válido
      } else {
        valid = (fieldValue).every(val =>
          (options as OptionType[]).some(o => o.value === val)
        );
      }
    } else {
      // Para valores simples, verificar que el valor exista en las opciones
      valid = (options as OptionType[]).some(o => o.value === fieldValue);
    }

    if (!valid && many && typeof valueLabel === 'string' && valueLabel.trim() !== '') {
      if (!valueLabel) logError('RenderFormField - *!valueLabel* - field: ', field, 'valueLabel: ', valueLabel, 'initialValue: ', initialValue);
      const res = getLabelValidateField(valueLabel as string, options as OptionType[]);
      if (res.value !== undefined) {
        setFieldValue(res.value);
        setValueLabel(res.label);
        handleChange(field.name, res.value, res.label);
        valid = true;
      }
    }

    if (!valid) {
      logError('RenderFormField - Valor inválido detectado:', {
        fieldName: field.name,
        fieldValue,
        isArray: Array.isArray(fieldValue),
        arrayLength: Array.isArray(fieldValue) ? fieldValue.length : 'N/A',
        availableOptions: (options as OptionType[]).map(o => o.value),
        willReset: true
      });
      resetField();
    } /* else {
      // TODO: SOLO PARA DEBUG
      logError('RenderFormField - Valor válido confirmado:', {
        fieldName: field.name,
        fieldValue,
        isArray: Array.isArray(fieldValue),
        arrayLength: Array.isArray(fieldValue) ? fieldValue.length : 'N/A'
      });
    }/**/
  }, [options]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -------- handlers -------- */
  const resetField = useCallback(() => {
    setFieldValue(undefined);
    handleChange(field.name, undefined);
  }, [field.name, handleChange]);

  /** Handlers */
  const onInternalChange = (value: FieldValue) => {
    if (fieldValue === value) return;
    setFieldValue(value);
    const label = (options as OptionType[] | undefined)?.find((o) => o.value === value)?.label;
    if (many && label) setValueLabel(label);
    handleChange(field.name, value, label);
  };

  const handleDateRangeChange = (value: Dayjs[] | null) => {
    if (!value || value.length < 2 || !value[0] || !value[1]) {
      handleChange(`${field.name}__gte`, undefined);
      handleChange(`${field.name}__lte`, undefined);
      setFieldValue(undefined);
      return;
    }

    handleChange(`${field.name}__gte`, dayjs(value[0]).toISOString());
    handleChange(`${field.name}__lte`, dayjs(value[1]).toISOString());
    setFieldValue(value as unknown as FieldValue);
  };

  /** Focus helpers */
  const handleFocus = () => setFieldFocused(true);
  const handleBlur = () => setFieldFocused(false);

  /** Search handler for autocomplete */
  const handleSearch = (value: string) => {
    if (handleAutoComplete && !fieldFocused) handleAutoComplete(field.name, value);
  };

  /** JSX */
  if (field.hidden) {
    return null;
  }

  const placeholder = field.placeholder ?? field.label;
  const prefixedName = `${fieldKey}_${field.name}`;
  const { help: formItemHelp, ...formItemProps } = (field.formItemProps as any) ?? {};
  const hasError = Boolean(errorMessage);
  const hasHelp = Boolean(formItemHelp);
  const errorId = `field-${field.name}-error`;
  const helpId = `field-${field.name}-help`;
  const describedBy = [hasError ? errorId : null, hasHelp ? helpId : null]
    .filter(Boolean)
    .join(' ') || undefined;
  const rawInputProps = (field.inputProps ?? {}) as Record<string, unknown>;
  const inputId = (rawInputProps.id as string | undefined) ?? `field-${field.name}`;
  const describedByValues = [
    rawInputProps['aria-describedby'] as string | undefined,
    describedBy,
  ]
    .filter(Boolean)
    .join(' ') || undefined;
  const ariaInvalidValue = (rawInputProps['aria-invalid'] as string | undefined) ?? (hasError ? 'true' : undefined);
  const ariaInvalid: "true" | "false" | undefined = (ariaInvalidValue === 'true' || ariaInvalidValue === 'false') ? ariaInvalidValue : (ariaInvalidValue ? 'true' : undefined);

  const resolvedInputProps = {
    ...rawInputProps,
    id: inputId,
    'aria-describedby': describedByValues,
    'aria-invalid': ariaInvalid,
  };
  const helpNode = hasError || hasHelp ? (
    <div>
      {hasError && <span id={errorId}>{errorMessage}</span>}
      {hasHelp && <span id={helpId}>{formItemHelp}</span>}
    </div>
  ) : undefined;
  let inputElement: React.ReactNode;

  switch (field.type) {
    /* boolean --------------------------------------------------- */
    case 'boolean':
      inputElement = (
        <Form.Item
          key={fieldKey}
          label={field.label}
          name={prefixedName}
          className="mb-0"
          initialValue={initialValue}
          validateStatus={errorMessage ? 'error' : ''}
          help={helpNode}
          valuePropName="checked"
          {...formItemProps}
        >
          <Switch
            className={field.className}
            onChange={onInternalChange}
            checked={fieldValue as boolean}
            disabled={disabled}
            {...(resolvedInputProps as any)}
          />
        </Form.Item>
      );
      break;

    /* search ---------------------------------------------------- */
    case 'search':
      inputElement = (
        <Form.Item
          key={fieldKey}
          label={field.label}
          name={prefixedName}
          className="mb-0"
          initialValue={initialValue}
          validateStatus={errorMessage ? 'error' : ''}
          help={helpNode}
          {...formItemProps}
        >
          <Search
            placeholder={placeholder}
            className={field.className}
            onChange={(e) => onInternalChange(e.target.value)}
            value={fieldValue as string | number}
            disabled={disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...(resolvedInputProps as any)}
          />
        </Form.Item>
      );
      break;

    /* select / select-with-search ------------------------------- */
    case 'select':
    case 'select-with-search':
      inputElement = (
        <Form.Item
          key={fieldKey}
          label={field.label}
          name={prefixedName}
          className="mb-0"
          initialValue={initialValue}
          validateStatus={errorMessage ? 'error' : ''}
          help={helpNode}
          {...formItemProps}
        >
          <Select
            showSearch={field.type === 'select-with-search'}
            allowClear={field.type === 'select-with-search'}
            placeholder={placeholder}
            onChange={onInternalChange}
            onSearch={field.type === 'select-with-search' ? handleSearch : undefined}
            onClear={() => onInternalChange(undefined)}
            className={field.className}
            value={fieldValue}
            disabled={disabled}
            filterOption={field.type === 'select-with-search' ? filterOption : undefined}
            filterSort={field.type === 'select-with-search' ? (a, b) => handleOptionsSort(a as OptionType, b as OptionType) : undefined}
            onFocus={handleFocus}
            onBlur={handleBlur}
            popupRender={field.dropdownRender}
            {...(resolvedInputProps as any)}
          >
            {options && (options as OptionType[]).filter(opt => opt.value !== null).map((opt) => (
              <Select.Option
                key={`${opt.value}`}
                value={opt.value}
                label={opt.label}
                disabled={opt.disabled}
              >
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      );
      break;

    /* tree-select ---------------------------------------------- */
    case 'tree-select':
      inputElement = (
        <Form.Item
          key={fieldKey}
          label={field.label}
          name={prefixedName}
          className="mb-0"
          initialValue={initialValue}
          validateStatus={errorMessage ? 'error' : ''}
          help={helpNode}
          {...formItemProps}
        >
          <TreeSelect
            treeData={options as TreeOptionFieldType[]}
            placeholder={placeholder}
            onChange={onInternalChange}
            value={fieldValue}
            disabled={disabled}
            treeCheckable
            showCheckedStrategy={TreeSelect.SHOW_PARENT}
            className={field.className}
            onFocus={handleFocus}
            onBlur={handleBlur}
            popupRender={field.dropdownRender}
            {...(resolvedInputProps as any)}
          />
        </Form.Item>
      );
      break;

    /* date_between --------------------------------------------- */
    case 'date_between':
      inputElement = (
        <Form.Item
          key={fieldKey}
          label={field.label}
          name={prefixedName}
          className="mb-0"
          initialValue={initialValue}
          validateStatus={errorMessage ? 'error' : ''}
          help={helpNode}
          {...formItemProps}
        >
          <RangePicker
            className={field.className}
            onChange={(val) => handleDateRangeChange(val as Dayjs[])}
            value={fieldValue as [Dayjs, Dayjs]}
            disabled={disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...(resolvedInputProps as any)}
          />
        </Form.Item>
      );
      break;

    /* date ------------------------------------------------------ */
    case 'date':
      inputElement = (
        <Form.Item
          key={fieldKey}
          label={field.label}
          name={prefixedName}
          className="mb-0"
          initialValue={initialValue}
          validateStatus={errorMessage ? 'error' : ''}
          help={helpNode}
          {...formItemProps}
        >
          <DatePicker
            className={field.className}
            onChange={onInternalChange}
            value={fieldValue as Dayjs}
            disabled={disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...(resolvedInputProps as any)}
          />
        </Form.Item>
      );
      break;

    /* text-area ------------------------------------------------- */
    case 'text-area':
      inputElement = (
        <Form.Item
          key={fieldKey}
          label={field.label}
          name={prefixedName}
          className="mb-0"
          validateStatus={errorMessage ? 'error' : ''}
          help={helpNode}
          initialValue={initialValue}
          {...formItemProps}
        >
          <Input.TextArea
            value={fieldValue as string}
            className={field.className}
            placeholder={placeholder}
            onChange={(e) => onInternalChange(e.target.value)}
            disabled={disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...(resolvedInputProps as any)}
          />
        </Form.Item>
      );
      break;

    /* rich-text ------------------------------------------------- */
    case 'rich-text':
      inputElement = (
        <Form.Item
          key={fieldKey}
          label={field.label}
          name={prefixedName}
          className="mb-0"
          validateStatus={errorMessage ? 'error' : ''}
          help={helpNode}
          {...formItemProps}
        >
          <SlateEditor
            initialValue={(initialValue as string) ?? ''}
            value={typeof fieldValue === 'string' ? fieldValue : undefined}
            className={field.className}
            onChange={onInternalChange}
            {...(resolvedInputProps as any)}
          />
        </Form.Item>
      );
      break;

    /* float ----------------------------------------------------- */
    case 'float':
      inputElement = (
        <Form.Item
          key={fieldKey}
          label={field.label}
          name={prefixedName}
          className="mb-0"
          initialValue={initialValue}
          validateStatus={errorMessage ? 'error' : ''}
          help={helpNode}
          {...formItemProps}
        >
          <Input
            type="number"
            className={field.className}
            placeholder={placeholder}
            onChange={(e) => onInternalChange(e.target.value)}
            value={fieldValue as number}
            disabled={disabled}
            step="0.01"
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...(resolvedInputProps as any)}
          />
        </Form.Item>
      );
      break;

    /* phone number --------------------------------------------- */
    case 'phonenumber':
      inputElement = (
        <Form.Item
          key={fieldKey}
          label={field.label}
          name={prefixedName}
          className="mb-0"
          initialValue={initialValue}
          validateStatus={errorMessage ? 'error' : ''}
          help={helpNode}
          {...formItemProps}
        >
          <PhoneInputCore
            className={`form-control ${field.className || ''}`}
            placeholder={placeholder}
            onChange={onInternalChange}
            value={fieldValue as string}
            disabled={disabled}
            defaultCountry="AR"
            onFocus={handleFocus}
            onBlur={handleBlur}
            metadata={metadata}
            {...(resolvedInputProps as any)}
          />
        </Form.Item>
      );
      break;

    /* image (drag & drop upload) --------------------------------
       * Reutiliza tu componente UploadDragerSorteable existente   */
    case 'image':
      inputElement = (
        <Form.Item
          key={fieldKey}
          label={field.label}
          name={prefixedName}
          className="mb-0"
          initialValue={initialValue}
          validateStatus={errorMessage ? 'error' : ''}
          help={helpNode}
          {...formItemProps}
        >
          <UploadDragerSorteable
            name={prefixedName}
            className={field.className}
            handleChange={handleChange}
            handleRemove={handleRemove}
            initialValue={initialValue as unknown as UploadDragerSorteableProps['initialValue']}
            accept={['image/*']}
            {...(resolvedInputProps as any)}
          />
        </Form.Item>
      );
      break;

    /* default (texto simple) ----------------------------------- */
    default:
      inputElement = (
        <Form.Item
          key={fieldKey}
          label={field.label}
          name={prefixedName}
          className="mb-0"
          initialValue={initialValue}
          validateStatus={errorMessage ? 'error' : ''}
          help={helpNode}
          {...formItemProps}
        >
          <Input
            type={field.type}
            className={field.className}
            placeholder={placeholder}
            onChange={(e) => onInternalChange(e.target.value)}
            value={fieldValue as string | number}
            disabled={disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...(resolvedInputProps as any)}
          />
        </Form.Item>
      );
  }

  return (
    <Col className="mb-3 d-flex flex-column justify-content-end" {...field.colProps}>
      {inputElement}
    </Col>
  );
};

/**
 * ---------------------------------------------
 *  GenericForm
 * ---------------------------------------------
 */
interface GenericFormProps {
  prefixKey?: string;
  formFieldsList: FieldType[];
  initFieldsValues?: { [key: string]: FieldValue };
  onChange: (name: string, value: FieldValue, label?: LabelType) => void;
  handleRemove?: (name: string, value: FieldValue) => void;
  handleAutoComplete?: (name: string, value: FieldValue) => void;
  handleGlobalAutoComplete?: (values: { [key: string]: FieldValue }) => void;
  disabledFields?: { [key: string]: boolean };
}

export const GenericForm: React.FC<GenericFormProps> = ({
  prefixKey,
  initFieldsValues,
  formFieldsList,
  onChange,
  handleRemove,
  handleAutoComplete,
  handleGlobalAutoComplete,
  disabledFields = {},
}) => {
  const [formValues, setFormValues] = useState<{ [key: string]: FieldValue }>({});
  const autoCompleteTimer = useRef<NodeJS.Timeout | null>(null);

  // Para evitar re-empujar iniciales en cada render (y bucles),
  // guardamos un hash del objeto de iniciales.
  const prevInitHashRef = useRef<string | null>(null);

  // logError('GenericForm - initFieldsValues: ', initFieldsValues);

  /** Throttled global autocomplete */
  useEffect(() => {
    if (!handleGlobalAutoComplete) return;
    if (autoCompleteTimer.current) clearTimeout(autoCompleteTimer.current);
    autoCompleteTimer.current = setTimeout(() => handleGlobalAutoComplete(formValues), 300);
    return () => {
      if (autoCompleteTimer.current) clearTimeout(autoCompleteTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleGlobalAutoComplete, formValues]);

  /**
   * Empuja los valores iniciales hacia arriba usando SIEMPRE el `internalName`
   * para alinear con los Form.Item del hijo. Solo corre cuando realmente
   * cambian las iniciales (según hash) o cambia la lista de campos/prefix.
   * Importante: **NO** depende de `onChange` para evitar bucles.
   */
  useEffect(() => {
    if (!initFieldsValues) return;

    const initHash = JSON.stringify(initFieldsValues);
    if (prevInitHashRef.current === initHash) return;

    const nextLocal: { [k: string]: FieldValue } = {};

    formFieldsList.forEach((field, idx) => {
      const key = prefixKey ? `${prefixKey}-${idx}` : `${idx}`;
      const internalName = `${key}_${field.name}`;
      const initVal = initFieldsValues[field.name];

      // (opcional) log para verificar campo por campo
      // logError('GenericForm - ', field.name, ' : ', initVal);

      if (initVal !== undefined) {
        // Solo emite si difiere de lo último que tenemos localmente
        if (formValues[internalName] !== initVal) {
          onChange(internalName, initVal);
        }
        nextLocal[internalName] = initVal;
      }
    });

    // Sincroniza el local map con los nombres internos usados por los hijos
    if (Object.keys(nextLocal).length) {
      setFormValues((prev) => ({ ...prev, ...nextLocal }));
    }

    prevInitHashRef.current = initHash;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initFieldsValues, formFieldsList, prefixKey]);

  const internalChange = useCallback(
    (name: string, value: FieldValue, label?: LabelType) => {
      logInfo('GenericForm.handleChange', { name, value, label });
      onChange(name, value, label);
      setFormValues((prev) => ({ ...prev, [name]: value }));
    },
    [onChange],
  );

  return (
    <Row gutter={24} className="justify-content-between">
      {formFieldsList.map((field, idx) => {
        const key = prefixKey ? `${prefixKey}-${idx}` : `${idx}`;
        // nombre interno REAL que usa RenderFormField en Form.Item
        const internalName = `${key}_${field.name}`;
        // tus iniciales llegan por clave "externa" (sin índice)
        const initVal = initFieldsValues?.[field.name];

        // (opcional) log para verificar
        // logError('GenericForm - ', field.name, ' : ', initVal);

        return (
          <RenderFormField
            key={internalName}
            fieldKey={key}
            field={field}
            many={field.many}
            initialValue={initVal}
            // IMPORTANTÍSIMO: ignoramos el name que manda el hijo y
            // subimos SIEMPRE el internalName para alinear con Form.Item
            handleChange={(_, v, l) => internalChange(internalName, v, l)}
            handleRemove={(_, v) => handleRemove?.(internalName, v)}
            handleAutoComplete={(_, v) => {
              if (handleAutoComplete) handleAutoComplete(internalName, v);
              else setFormValues((p) => ({ ...p, [internalName]: v }));
            }}
            disabled={disabledFields[field.name]}
          />
        );
      })}
    </Row>
  );
};
